import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@radix-ui/react-dropdown-menu"
import { CheckIcon } from "@radix-ui/react-icons"
import { cn, tv } from "@utils/tw"
import { forwardRef, useEffect, useRef, useState } from "react"
import { Button } from "./Button"
import { Icon } from "./Icon"
import { inputStyles } from "./Input"
import { dropdownItemStyles, dropdownMenuStyles } from "./menu-helpers"

type FilterItemDefinition = {
  value: string
  label: string
}
interface FilterProps {
  items: FilterItemDefinition[]
  value?: string[]
  onValueChange?: (value: string[]) => void
  placeholder?: string
  type?: "dropdown" | "list"
  disabled?: boolean
  showCount?: boolean
  className?: string
  align?: "start" | "center" | "end"
  side?: "top" | "right" | "bottom" | "left"
}

export type MultiSelectType = HTMLDivElement & {
  setValue: (value: string[]) => void
}

export const MultiSelect = forwardRef<MultiSelectType, FilterProps>(
  (
    {
      items,
      value,
      onValueChange,
      placeholder,
      type = "dropdown",
      disabled,
      showCount = true,
      className,
      align = "start",
      side = "bottom",
    },
    outerRef,
  ) => {
    const ref = useRef<HTMLDivElement>(null)
    const styles = selectStyles({})
    const dropdownStyles = dropdownMenuStyles()
    const itemStyles = dropdownItemStyles()
    const [internalValue, setInternalValue] = useState<string[]>(value ?? [])

    const handleItemClick = function (item: FilterItemDefinition) {
      const newValue = internalValue.includes(item.value)
        ? internalValue.filter((value) => value !== item.value)
        : [...internalValue, item.value]
      setInternalValue(newValue)
      onValueChange?.(newValue)
    }

    useEffect(() => {
      setInternalValue(value || [])
    }, [value])

    return (
      <div ref={outerRef} className={cn("flex", className)}>
        {type === "dropdown" ? (
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild className={cn(styles.base())} disabled={disabled}>
              <Button variant="outline">
                <div className={cn(itemStyles.base())}>
                  <span className={styles.triggerLabel()}>{placeholder ?? "Select..."}</span>
                  {internalValue.length > 0 && showCount && (
                    <span className={styles.triggerCount()}>({internalValue.length})</span>
                  )}
                  <Icon name="chevronDown" />
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className={cn(
                dropdownStyles.content(),
                "min-w-[--radix-dropdown-menu-trigger-width] max-h-[--radix-dropdown-menu-content-available-height] overflow-auto",
              )}
              side={side}
              sideOffset={3}
              align={align}
              avoidCollisions
            >
              <div
                className={cn(itemStyles.base(), "cursor-pointer")}
                onClick={() => {
                  setInternalValue([])
                  onValueChange?.([])
                }}
              >
                <span className={styles.clearLabel()}>Clear</span>
              </div>

              {items.map((item) => {
                const checked = internalValue.includes(item.value)
                return (
                  <DropdownMenuCheckboxItem
                    key={item.value}
                    checked={checked}
                    onCheckedChange={() => handleItemClick(item)}
                    onSelect={(e) => e.preventDefault()}
                    className={cn(itemStyles.base(), "py-1")}
                  >
                    <div className="flex border border-solid border-grey2">
                      <span className={cn(itemStyles.indicator(), checked ? "opacity-100" : "opacity-0")}>
                        <CheckIcon />
                      </span>
                    </div>
                    <span className={itemStyles.label()}>{item.label}</span>
                  </DropdownMenuCheckboxItem>
                )
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <div className="w-full flex flex-col gap-2">
            {placeholder && <span className={styles.triggerLabel()}>{placeholder}</span>}
            <div
              onClick={() => {
                setInternalValue([])
                onValueChange?.([])
              }}
              className="cursor-pointer"
            >
              <span className={styles.clearLabel()}>Clear</span>
            </div>
            <div className="flex flex-col gap-1 w-full">
              {items.map((item) => {
                const checked = internalValue.includes(item.value)
                return (
                  <div
                    className="flex items-center gap-4 md:gap-1 cursor-pointer"
                    key={item.value}
                    onClick={() => handleItemClick(item)}
                  >
                    <div className="flex border border-solid border-grey2">
                      <span className={cn(itemStyles.indicator(), checked ? "opacity-100" : "opacity-0")}>
                        <CheckIcon />
                      </span>
                    </div>
                    <span className={itemStyles.label()}>{item.label}</span>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    )
  },
)

const selectStyles = tv({
  extend: inputStyles,
  base: "outline-none",
  slots: {
    triggerLabel: "type-label-m flex-auto text-left mt-[0.2em]",
    triggerCount: "type-label-xs",
    clearLabel: [dropdownItemStyles().label(), "type-label-xs"],
  },
  variants: {
    size: {
      default: "h-control px-2",
    },
  },
})
